/**
 * 埋め込みステージ設定の契約型（`05-database-schema.md` §3）。
 *
 * 書き手（ビルド時のページ生成 `src/build/pages.ts`）と読み手（ランタイムの
 * `shell/stage-config.ts`）が同じ型を共有するため、DOM に依存しないこのファイルに置く。
 * ビルド側は DOM の無い Node の型検査（tsconfig.node.json）で読まれる。
 */

/** 画像の配信形式。ビルドが AVIF / WebP を用意する。 */
export interface StageImageSources {
  readonly avif?: string;
  readonly webp?: string;
}

/** QR 刻印を置く盤面の隅（TK-058）。Pipeline D が画像の情報量から決める。 */
export type QrCorner = 'tl' | 'tr' | 'bl' | 'br';

/** 生成ページに埋め込まれる、そのステージだけの設定。 */
export interface EmbeddedStageConfig {
  readonly cols: number;
  readonly rows: number;
  readonly image: string | StageImageSources;
  /**
   * ピース別タイルのベース URL（TK-035）。タイル `p` は `${tiles}.t<2 桁>.webp`。
   * 盤面はタイルを貼り、原画像は完成表示（`board-complete`）まで DOM に現れない。
   * 無い場合（開発用の仮ステージ等）は原画像の切り出しで描く。
   */
  readonly tiles?: string;
  /**
   * ステージ固有の秘文字レイヤー SVG の URL（TK-038）。無ければ共通の仮素材で描く。
   */
  readonly secret?: string;
  /** 秘文字の目標配置 `τ`。長さ `cols × rows`。 */
  readonly secretLayout: number[];
  /** 初期配置の生成シード。 */
  readonly seed: number;
  /** シリーズのフォルダ連番（`001` 等）。進捗の記録に使う（FR-019、`05` §3）。 */
  readonly series: string;
  /** シリーズ内の位置（1 始まり）。 */
  readonly stageNo: number;
  /** シリーズの問題数。 */
  readonly stageCount: number;
  /** 次ステージの URL。最終ステージでは持たない。 */
  readonly next?: string;
  readonly isLast: boolean;
  /**
   * QR 刻印の隅（TK-058）。完成画像のうち最も情報量の少ない隅に、サイト URL の QR を
   * 不透明度 70% で刻印する（スクリーンショット経由の流入導線）。無ければ右下。
   */
  readonly qrCorner?: QrCorner;
}

/** 埋め込み設定を置く要素の id。 */
export const STAGE_CONFIG_ID = 'stage-config';
